import { CircleText, QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import {
  DragItem,
  FloatingDraggableBox,
} from '@doltech/ui/lib/figma/FloatingDraggableBox/FloatingDraggableBox';
import {
  QUESTION_NO_WRAPPER_MARGIN_BOTTOM,
  QuestionTypeEnum,
  SkillEnum,
  TestTypeEnum,
} from '@doltech/utils/lib/constants';
import cl from 'classnames';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useUpdate } from 'react-use';
import styled from 'styled-components';

import { KeyValueModelSnapshotIn } from '../../question-data-model/key-value.store';
import { Heading } from '../CompletionNoHintAnswer';

const FLOATING_WIDTH = 264;

export const DragDropWrapper = styled(motion.div)<{ skill }>`
  position: relative;
  background: ${colorsV2.white100};
  box-sizing: border-box;
  box-shadow: 0 2px 2px ${colorsV2.blackShadow4};
  border-radius: 16px;
  padding: 16px;

  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr ${FLOATING_WIDTH}px;
  gap: 16px;

  .question-no-wrapper {
    margin-bottom: ${QUESTION_NO_WRAPPER_MARGIN_BOTTOM}px;
  }

  .floating-right {
    position: sticky;
    background-color: ${colorsV2.white100};
    top: 30px;
    right: 20px;
    width: ${FLOATING_WIDTH}px;
    border-radius: 8px;
    box-shadow: 0 16px 24px ${colorsV2.blackShadow6}, 0 2px 6px ${colorsV2.blackShadow4},
      0 0 1px ${colorsV2.blackShadow4};

    &.${SkillEnum.LISTENING} {
      top: 0;
      left: calc(100% + 8px);
    }
  }

  .question-guideline-header {
    margin-bottom: 16px;
    .guideline-title {
      font-weight: 500;
      font-size: 18px;
      line-height: 24px;
      color: ${colorsV2.black100};
    }
    .guideline-desc {
      font-size: 12px;
      line-height: 16px;
      color: ${colorsV2.gray100};
      .emo-desc {
        margin-right: 4px;
      }
    }
  }
`;

const QuestionContainer = styled(motion.div)`
  width: 100%;
`;

const QuestionWrapper = styled.div`
  padding: 0;
`;

interface DragOption {
  title: string;
  icon: string;
  options: any;
}

interface DropResult {
  dragData: any;
  dropData: any;
}

export interface DragDropComponentProps extends React.HTMLAttributes<HTMLDivElement> {
  no?: string | number;
  id?: string;
  dragOption?: DragOption;
  accept?: string;
  data?: any;
  heading?: string;
  userAnswers?: any;
  onRemoveAnswerAtIndex?: (optionIndex: number) => void;
  dropOption?: KeyValueModelSnapshotIn[];
  onDropSuccess?: (input: DropResult) => void;
  paragraph?: any;
  currentSection?: any;
  testType?: TestTypeEnum;
  placeholder?: string;
}

export const DragDropComponent = observer((props: DragDropComponentProps) => {
  const { id, dragOption, accept, children, heading, currentSection, testType } = props;
  const [isChildDrag, setIsChildDrag] = React.useState(false);
  const { skill } = currentSection;
  const constraintsRef = React.useRef(null);
  const [showOptions, setShowOptions] = React.useState(false);
  const [maxHeight, setMaxHeight] = React.useState(0);

  const forceUpdate = useUpdate();

  React.useEffect(() => {
    const element = document.getElementById(id);
    let maxH;
    if (element) {
      if (skill === SkillEnum.READING) {
        const height =
          52 + // Header height.
          16 + // body-container top padding.
          59 + // header-container height and margin bottom.
          16 * 2 + // question-no-xx top padding.
          (testType === TestTypeEnum.PRACTICE_TEST ? 56 : 120) + // Footer height.
          16 * 2 + // Floating box padding top and bottom.
          40 + // Floating box header.
          8; // Space to footer.
        maxH = window.innerHeight - height;
      } else {
        // LISTENING.
        const questionHeight = element.clientHeight;
        if (questionHeight > window.innerHeight - 230) {
          maxH = window.innerHeight - 331;
        } else {
          maxH = questionHeight - 101;
        }
      }

      if (
        skill === SkillEnum.READING &&
        (accept === QuestionTypeEnum.MATCHING_FEATURE_NAME ||
          accept === QuestionTypeEnum.MATCHING_HEADING ||
          accept === QuestionTypeEnum.MATCHING_ENDING ||
          accept === QuestionTypeEnum.MATCHING_PARAGRAPH) &&
        maxH > 560
      ) {
        maxH = 560;
      }
      setMaxHeight(maxH);
      setShowOptions(true);
    }
  }, [accept, id, maxHeight, skill, testType]);

  return (
    <QuestionContainer ref={constraintsRef}>
      <QuestionWrapper className="dol-question">
        <DragDropWrapper id={id} className="drag-area" skill={skill}>
          {heading && <Heading className="heading">{heading}</Heading>}
          {children}
          {showOptions && (
            <motion.div
              className={cl('floating-right', skill)}
              drag={!isChildDrag}
              dragPropagation
              dragElastic={0.2}
              dragConstraints={constraintsRef}
              onDrag={(e) => e.preventDefault()}
              onDragEnd={() => {
                forceUpdate();
              }}
            >
              <FloatingDraggableBox title="Kéo option vào câu hỏi">
                {dragOption.options.map((option) => {
                  const { key, circleText, text } = option;
                  return (
                    <DragItem
                      id={text}
                      onDragEnd={() => {
                        setIsChildDrag(false);
                        forceUpdate();
                      }}
                      onMouseDown={() => {
                        setIsChildDrag(true);
                      }}
                      key={key}
                      dragData={{ option }}
                      accept={accept}
                    >
                      <CircleText
                        displayMode={QuestionDisplayMode.QUESTION_OPTION_DRAG}
                        circleText={circleText}
                        text={text}
                      />
                    </DragItem>
                  );
                })}
              </FloatingDraggableBox>
            </motion.div>
          )}
        </DragDropWrapper>
      </QuestionWrapper>
    </QuestionContainer>
  );
});
